import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { Avatar } from "@mui/material";
import clsx from "clsx";
import { useEffect, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAlert } from "../../contexts/AlertProvider";
import { useUser } from "../../contexts/UserProvider";
import ProfilePage from "../../pages/profile-page/ProfilePage";
import "./Sidebar.scss";
import { ImageLoaded } from "../image-loaded/ImageLoaded";
import randomColor from "randomcolor";
import { useOrganization } from "../../contexts/OrganizationProvider";

export interface SideBarButton {
  title: string;
  icon?: ReactNode;
  navigate: string;
  moreRoutes?: string[];
  children?: SideBarButton[];
}

interface SideBarProps {
  buttons: SideBarButton[];
}

export const Sidebar: React.FC<SideBarProps> = (props) => {
  const { buttons } = props;
  const navigate = useNavigate();
  const [selectedButton, setSelectedButton] = useState<number>();
  const [isMenuOpen, setIsMenuOpen] = useState(true);
  const [profilePageOpen, setProfilePageOpen] = useState<boolean>(false);
  const { setIsSidebarOpen, isSidebarOpen } = useAlert();
  const [isHover, setIsHover] = useState(false);
  const { currentUser } = useUser();
  const { currOrganization } = useOrganization();
  const location = useLocation();

  useEffect(() => {
    setIsMenuOpen(isSidebarOpen);
  }, [isSidebarOpen]);

  useEffect(() => {
    if (isHover) {
      const timeout = setTimeout(() => {
        setIsSidebarOpen(isHover);
      }, 1200);
      return () => clearTimeout(timeout);
    } else return setIsSidebarOpen(isHover);
  }, [isHover]);

  function handleButtonClick(btn: SideBarButton, index: number) {
    setSelectedButton(index);
    if (btn.children && isMenuOpen) return;
    navigate(String(btn.navigate));
    setIsHover(false);
  }

  useEffect(() => {
    setSelectedButton(
      buttons.findIndex(
        (button) =>
          button.navigate === location.pathname ||
          button.moreRoutes?.some((route) => location.pathname.includes(route)),
      ),
    );
  }, [location.pathname, buttons]);

  return (
    <>
      <div
        onMouseOver={() => setIsHover(true)}
        onMouseOut={() => setIsHover(false)}
        onMouseLeave={() => {
          setIsHover(false);
        }}
        className={clsx("sidebar-main-container", isMenuOpen && "open")}
      >
        <div className="logo-buttons-container">
          <img
            src={isMenuOpen ? "/weme-sidebar-logo.svg" : "/weme-small-logo.svg"}
            alt="לוגו WEME"
            className={clsx("logo", [
              isMenuOpen ? "weme-logo-big" : "weme-logo",
            ])}
          />
          <div className="buttons-container">
            {buttons.map((btn, index) => {
              return (
                <div
                  className={clsx(["sidebar-button"])}
                  onClick={() => {
                    handleButtonClick(btn, index);
                  }}
                  key={index}
                >
                  <div
                    className={clsx(
                      "content",
                      selectedButton === index && "selected",
                    )}
                  >
                    {btn.icon && btn.icon}
                    {isMenuOpen && (
                      <div className="button-title">{btn.title}</div>
                    )}
                    {btn.children && isMenuOpen && (
                      <KeyboardArrowLeftIcon
                        className="arrow"
                        style={{
                          transform:
                            selectedButton === index ? "rotate(-90deg)" : "",
                        }}
                      />
                    )}
                  </div>
                  {btn.children &&
                    selectedButton === index &&
                    isMenuOpen &&
                    btn.children.map((childBtn) => {
                      return (
                        <div className="child-container" key={childBtn.title}>
                          <button
                            onClick={() => handleButtonClick(childBtn, index)}
                            className={clsx(
                              "child-button",
                              location.pathname === childBtn.navigate &&
                              "selected",
                            )}
                          >
                            {childBtn.title}
                          </button>
                        </div>
                      );
                    })}
                </div>
              );
            })}
          </div>
        </div>
        <div className="bottom-container">
          <div
            onClick={() => setProfilePageOpen((prev) => !prev)}
            className={clsx("profile-name-container", isMenuOpen && "open")}
          >
            <ImageLoaded
              imageId={currentUser?.profileImageId || undefined}
              alt={currentUser?.name}
              styleObject={{
                width: "2rem",
                height: "2rem",
                borderRadius: "0.8rem",
                backgroundColor: randomColor({
                  luminosity: "light",
                  seed: currentUser?.id,
                }),
              }}
            />
            {isMenuOpen && <p style={{ color: "GrayText" }}>{currOrganization?.name}</p>}
          </div>
        </div>
      </div>
      <ProfilePage open={profilePageOpen} setOpen={setProfilePageOpen} />
    </>
  );
};
